import AppError from '@/components/AppError';
import { TappIcon } from '@/components/ui/TappIcon';
import { TappInputFilter } from '@/components/ui/TappInputFilter';
import { TappLink } from '@/components/ui/TappLink';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappPaginator } from '@/components/ui/TappPaginator';
import { useApiTable, TappTableWrapper, TappTable } from '@/components/ui/TappTable';
import { cn } from '@/components/ui/utils';
import { Company } from '@/lib/models/company';
import { ApiUtils } from '@/lib/utils/api';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert, useConfirm } from '@/components/ui/alert-dialog-provider';

const CompaniesPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader
        title={t('companies')}
        backLink="/admin"
        actions={
          <TappLink
            to="/admin/companies/new"
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            {t('addCompany')}
          </TappLink>
        }
      />
      <Table />
    </>
  );
};

const TableFilters = () => {
  const { t } = useTranslation();

  return (
    <section className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <TappInputFilter
        name="search"
        border
        placeholder={t('searchCompanies')}
        prefix={<TappIcon>search</TappIcon>}
        className="min-w-[240px]"
        clearable
      />
    </section>
  );
};

const Table = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const alert = useAlert();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteCompany, isPending: isDeleting } = useMutation({
    mutationFn: (companyId: string) => ApiUtils.delete(`company/${companyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const handleDeleteClick = async (company: Company) => {
    const ok = await confirm({
      title: t('deleteCompany'),
      body: t('deleteCompanyConfirmBody', { name: company.name }),
      cancelButton: t('cancel'),
      actionButton: t('confirmDelete'),
    });

    if (!ok) return;

    try {
      await deleteCompany(company._id);
    } catch (err) {
      console.error(err);
      await alert({
        title: t('error'),
        body: ApiUtils.convertApiErrorToString(err),
      });
    }
  };

  const columnHelper = createColumnHelper<Company>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('name'),
        enableSorting: true,
      }),
      columnHelper.accessor('subaccountId', {
        header: t('subaccountId'),
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          return value || '-';
        },
      }),
      columnHelper.accessor('dayTariff', {
        header: t('dayTariff'),
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? `${value.toFixed(2)} lei/km` : '-';
        },
      }),
      columnHelper.accessor('nightTariff', {
        header: t('nightTariff'),
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? `${value.toFixed(2)} lei/km` : '-';
        },
      }),
      columnHelper.display({
        id: 'delete',
        header: '',
        cell: ({ row: { original } }) => (
          <button
            className="text-red-600 hover:underline"
            onClick={() => handleDeleteClick(original)}
            disabled={isDeleting}
          >
            {t('delete')}
          </button>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <div className="flex items-center justify-end">
            <TappLink to={`/admin/companies/${original._id}`}>{t('edit')}</TappLink>
          </div>
        ),
      }),
    ],
    [t, isDeleting]
  );

  const { table, isLoading, total, error } = useApiTable<Company>({
    columns,
    queryKey: (filter) => ['companies', filter],
    queryFn: async (filter) => {
      const response = await ApiUtils.get<Company[]>('company', {
        query: { ...filter },
      });
      const companies = Array.isArray(response) ? response : [];
      return { data: companies, total: companies.length };
    },
    enableSortingRemoval: true,
  });

  if (error) return <AppError error={error} />;

  return (
    <>
      <TableFilters />
      <TappTableWrapper className={cn({ isLoading })}>
        <TappTable table={table} stickyHeader />
        <TappPaginator table={table} total={total} />
      </TappTableWrapper>
    </>
  );
};

export default CompaniesPage;
